import { C1_DOOR_TYPE_UI_LABELS, C1_GARAGE_DOOR_TYPE_UI_LABELS, C1_WINDOW_SHADING_LABELS } from "../C1_uiLabels";
import { itx } from "../../../src/translationItx/itx";

import { getOpeningEloProfile } from "../data/C1_OpeningEloDefinitions";
import { furnitureById } from "../furniture/C1_furnitureRegistry";
import type { C1_Furniture } from "../model/C1_Furniture";
import type { C1_Opening } from "../model/C1_Opening";
import type { C1_Room } from "../model/C1_Room";
import { reconcileEloEquipmentInstance } from "../model/C1_ensureEloEquipmentDefaults";
import c1_state from "../model/C1_State";
import type { C1_FurniturePropertyDefinition } from "../types/C1_FurnitureTypes";
import type { DoorType, GarageDoorType, WindowShading } from "../types/C1_ModelTypes";


export type C1_RaumbuchObject =
  | { type: "room"; value: C1_Room }
  | { type: "opening"; value: C1_Opening }
  | { type: "furniture"; value: C1_Furniture };

export function c1RoombookObjectDetailsText(object: C1_RaumbuchObject, language: "de" | "en"): string {
  if (object.type === "room") return "";
  if (object.type === "opening") {
    const opening = object.value;
    const details: string[] = [];
    if (opening.kind === "door") {
      details.push(`${itx("c1.ui.Turtyp", "Türtyp")}: ${C1_DOOR_TYPE_UI_LABELS[opening.doorType]}`);
      if (opening.doorType !== "passage") {
        details.push(`${itx("c1.ui.Schiebetur", "Schiebetür")}: ${opening.slidingDoor ? itx("ProjectList.yes", "Ja") : itx("ProjectList.no", "Nein")}`);
        details.push(`${itx("c1.ui.Flugel", "Flügel")}: ${opening.doorLeafCount}`);
      }
    }
    if (opening.kind === "garage-door") {
      details.push(`${itx("c1.ui.Tortyp", "Tortyp")}: ${C1_GARAGE_DOOR_TYPE_UI_LABELS[opening.garageDoorType]}`);
    }
    if (opening.kind === "window" || opening.doorType === "garden") {
      details.push(
        `${itx("raumbuch.shade", "Beschattung")}: ${
          C1_WINDOW_SHADING_LABELS[opening.windowShading]
        }`,
      );
    }
    return details.join(" · ");
  }

  const furniture = object.value;
  const definition = furnitureById[furniture.definitionId];
  const propertyContext = { values: furniture.baseProps, wallDocked: furniture.wallDocked !== undefined };
  return (
    definition?.properties
      .map((property) => {
        const value = furniture.baseProps[property.key] ?? property.defaultValue;
        if (value === undefined) return "";
        if (property.type === "boolean") {
          return `${property.label}: ${value ? (itx("ProjectList.yes", "Ja")) : itx("ProjectList.no", "Nein")}`;
        }
        const optionLabel =
          property.enum?.[String(value)] ??
          (typeof property.options === "function" ? property.options(propertyContext) : property.options)?.find(
            (option) => String(option.value) === String(value),
          )?.label;
        return `${property.label}: ${optionLabel ?? value}${property.unit ? ` ${property.unit}` : ""}`;
      })
      .filter(Boolean)
      .join(" · ") ?? ""
  );
}

function FurnitureProperty({
  furniture,
  property,
}: {
  furniture: C1_Furniture;
  property: C1_FurniturePropertyDefinition;
}) {
  const value = furniture.baseProps[property.key] ?? property.defaultValue;
  const propertyContext = { values: furniture.baseProps, wallDocked: furniture.wallDocked !== undefined };
  const options = typeof property.options === "function" ? property.options(propertyContext) : property.options;
  const update = (rawValue: string | boolean) => {
    const nextValue = property.type === "number" ? Number(rawValue) : rawValue;
    furniture.setBaseProps({ ...furniture.baseProps, [property.key]: nextValue });
  };

  return (
    <div className="field">
      <div className="label">
        <span title={property.description}>{property.label}</span>
      </div>
      {property.type === "boolean" ? (
        <input type="checkbox" checked={Boolean(value)} onChange={(event) => update(event.target.checked)} />
      ) : property.enum ? (
        <select className="C1_editorSelect" value={String(value)} onChange={(event) => update(event.target.value)}>
          {Object.entries(property.enum).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
      ) : property.type === "select" ? (
        <select className="C1_editorSelect" value={String(value)} onChange={(event) => update(event.target.value)}>
          {options?.map((option) => (
            <option key={String(option.value)} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : property.type === "color" ? (
        <input type="color" value={String(value)} onChange={(event) => update(event.target.value)} />
      ) : (
        <input
          type="number"
          value={Number(value)}
          min={property.min}
          max={property.max}
          step={property.step}
          onChange={(event) => update(event.target.value)}
        />
      )}
    </div>
  );
}

export function C1_RaumbuchObjectDetails({ object }: { object: C1_RaumbuchObject }) {
  const language = c1_state.language;

  if (object.type === "room") return null;

  if (object.type === "furniture") {
    const furniture = object.value;
    const definition = furnitureById[furniture.definitionId];
    if (!definition?.properties.length) return null;
    return (
      <section className="C1_roombookObjectDetails" onBlur={() => state.undoManager.userInteractionEnded()}>
        <h2>{itx("c1.ui.Objektdetails", "Objektdetails")}</h2>
        {definition.properties.map((property) =>
          property.visible?.({ values: furniture.baseProps, wallDocked: furniture.wallDocked !== undefined }) ===
          false ? null : (
            <FurnitureProperty key={property.key} furniture={furniture} property={property} />
          ),
        )}
      </section>
    );
  }

  const opening = object.value;
  const garageDoor = opening.kind === "garage-door";
  const garageDoorTypeOptions: JSX.Element[] = [];
  if (garageDoor) {
    for (const garageDoorType of Object.keys(C1_GARAGE_DOOR_TYPE_UI_LABELS) as GarageDoorType[]) {
      garageDoorTypeOptions.push(
        <option key={garageDoorType} value={garageDoorType}>
          {C1_GARAGE_DOOR_TYPE_UI_LABELS[garageDoorType]}
        </option>,
      );
    }
  }
  const applyProfile = () => {
    const profile = getOpeningEloProfile(opening);
    opening.setEloTree(
      reconcileEloEquipmentInstance(
        profile.rootDefinitionId,
        profile.definitions,
        c1_state.project.equipmentStandard,
        opening.eloTree,
        c1_state.settings.buttonDefaults,
      ),
    );
  };

  return (
    <section className="C1_roombookObjectDetails">
      <h2>{itx("c1.ui.Objektdetails", "Objektdetails")}</h2>
      {opening.kind === "door" && (
        <>
          <div className="field">
            <div className="label">{itx("c1.ui.Turtyp", "Türtyp")}</div>
            <select
              className="C1_editorSelect"
              value={opening.doorType}
              onChange={(event) => {
                opening.setDoorType(event.target.value as DoorType);
                applyProfile();
                state.undoManager.userInteractionEnded();
              }}
            >
              <option value="interior">{itx("c1.ui.Innentur", "Innentür")}</option>
              <option value="entrance">{itx("c1.ui.Haustur", "Haustür")}</option>
              <option value="garden">{itx("c1.ui.Gartentur", "Gartentür")}</option>
              <option value="passage">{itx("c1.ui.Durchgang", "Durchgang")}</option>
            </select>
          </div>
          {opening.doorType !== "passage" && <>
              <div className="field">
                <div className="label">{itx("c1.ui.Schiebetur", "Schiebetür")}</div>
                <input
                  type="checkbox"
                  checked={opening.slidingDoor}
                  onChange={(event) => {
                    opening.setSlidingDoor(event.target.checked);
                    state.undoManager.userInteractionEnded();
                  }}
                />
              </div>
              <div className="field">
                <div className="label">{itx("c1.ui.Anzahl_Flugel", "Anzahl Flügel")}</div>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={opening.doorLeafCount}
                  onChange={(event) => opening.setDoorLeafCount(Number(event.target.value))}
                  onBlur={() => state.undoManager.userInteractionEnded()}
                />
              </div>
          </>}
        </>
      )}
      {garageDoor && (
        <div className="field">
          <div className="label">{itx("c1.ui.Tortyp", "Tortyp")}</div>
          <select
            className="C1_editorSelect"
            value={opening.garageDoorType}
            onChange={(event) => {
              opening.setGarageDoorType(event.target.value as GarageDoorType);
              state.undoManager.userInteractionEnded();
            }}
          >
            {garageDoorTypeOptions}
          </select>
        </div>
      )}
      {(opening.kind === "window" || opening.doorType === "garden") && (
        <div className="field">
          <div className="label">{itx("raumbuch.shade", "Beschattung")}</div>
          <select
            className="C1_editorSelect"
            value={opening.windowShading}
            onChange={(event) => {
              opening.setWindowShading(event.target.value as WindowShading);
              applyProfile();
              state.undoManager.userInteractionEnded();
            }}
          >
            {(Object.keys(C1_WINDOW_SHADING_LABELS) as WindowShading[]).map((option) => (
              <option key={option} value={option}>
                {C1_WINDOW_SHADING_LABELS[option]}
              </option>
            ))}
          </select>
        </div>
      )}
    </section>
  );
}
